import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Stack, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { v4 as uuidv4 } from 'uuid';

import { PhoneBase, InputUI, ButtonUI, CheckboxUI, SwitchUI, AutoCompleteSelectUI, FormLabelUI } from '../Interface';
import itemService from '../../service/item.service';
import { useLoader } from '../../provider/LoaderProvider';
import SearchDialog from './SearchDialog';
import { DocumentType } from '../../config/DocumentType';
import dayjs from 'dayjs';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import productionService from '../../service/production.service';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import ItemsDialog from './ItemsDialog';
import useUsers from '../../context/UsersContext';
import { ProductionIssueDirection } from '../../config/production';
import masterDataService from '../../service/masterData.service';

const ProductionMaterial = () => {
  const { getUser, user } = useUsers();
  const itemInputRef = useRef(null);
  const { config, getConfig } = useConfig();
  const { getParam, params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [openSearch, setOpenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [itemFilter, setItemFilter] = useState('');
  const [docLines, setDocLines] = useState([]);
  const [fullSearch, setFullSearch] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [issueDirection, setIssueDirection] = useState(ProductionIssueDirection.OUTGOING);
  const [targetWhsCode, setTargetWhsCode] = useState('');
  const [targetBinId, setTargetBinId] = useState(null);
  const [warehouseData, setWarehouseData] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  const isIncoming = issueDirection === ProductionIssueDirection.INCOMING;

  useEffect(() => {
    Object.keys(config).length === 0 && getConfig();
    Object.keys(params).length === 0 && getParam();
    Object.keys(user).length === 0 && getUser();

    masterDataService.warehouseToForm().then((data) => {
      const rows = data ?? [];
      setWarehouseData(rows);
      setWarehouseOptions(generateAutocompleteItems(rows, 'name', 'code'));
    });
  }, []);

  useEffect(() => {
    setDocLines([]);
  }, [selectedProduction]);

  useEffect(() => {
    setDocLines([]);
    setItems([]);
    setItemFilter('');
    if (issueDirection === ProductionIssueDirection.INCOMING) {
      setTargetWhsCode(selectedProduction?.whsCode || config?.defaultWarehouse || '');
    } else {
      setTargetWhsCode('');
      setTargetBinId(null);
    }
  }, [issueDirection]);

  useEffect(() => {
    if (!isIncoming) return;
    setTargetWhsCode(selectedProduction?.whsCode || config?.defaultWarehouse || '');
  }, [isIncoming, selectedProduction, config?.defaultWarehouse]);

  useEffect(() => {
    if (!isIncoming) {
      setTargetBinId(null);
      return;
    }

    const whs = warehouseData.find((warehouse) => warehouse.code === targetWhsCode);
    if (whs?.binManaged) {
      const hasSelectedBin = (whs.bin ?? []).some((bin) => bin.binId === targetBinId);
      if (!hasSelectedBin) {
        setTargetBinId(whs.dispatchBin ?? null);
      }
      return;
    }

    setTargetBinId(null);
  }, [isIncoming, targetWhsCode, targetBinId, warehouseData]);

  const defaultDocumentLine = {
    lineStatus: 'C',
    itemCode: null,
    itemName: null,
    itemCodeInBp: null,
    qty: null,
    cplQty: null,
    opnQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    isBillable: false,
    baseId: null,
    baseLine: null,
    baseDocnum: null,
    baseDocType: null,
    warranty: false,
    docLineBatchSn: [],
    docLineBin: [],
  };

  const defaultValues = {
    docTypeId: DocumentType.IssueForProduction,
    docStatus: 'C',
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: config?.defaultWarehouse || null,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const handleQtyChange = (id) => (e) => {
    const val = Math.max(0, Number(e.target.value));
    setDocLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;

        const nextBatch = Array.isArray(line.docLineBatchSn)
          ? line.docLineBatchSn.map((batch) => ({
              ...batch,
              quantity: val,
            }))
          : [];

        const nextBins = Array.isArray(line.docLineBin)
          ? line.docLineBin.map((bin) => ({
              ...bin,
              quantity: val,
            }))
          : [];

        return {
          ...line,
          qty: val,
          docLineBatchSn: nextBatch,
          docLineBin: nextBins,
        };
      })
    );
  };

  const handleOpenSearch = () => {
    setOpenSearch(true);
    setSearchQuery('');
  };

  const handleCloseSearch = () => {
    setOpenSearch(false);
    setTimeout(() => {
      if (itemInputRef.current) itemInputRef.current.focus();
    }, 0);
  };

  const addItem = (data) => {
    const resolvedQty = 1;
    const resolvedBatchId = data?.batchId ?? data?.snBinId ?? (data?.batchSn ? uuidv4() : null);
    const nextBatch =
      data?.batchSn && String(data.batchSn).trim() !== ''
        ? [
            {
              id: resolvedBatchId,
              batchSn: data.batchSn,
              quantity: resolvedQty,
            },
          ]
        : [];

    const hasBin = Boolean(data?.binCode) || Boolean(data?.binId);
    const nextBin = hasBin
      ? [
          {
            id: uuidv4(),
            binCode: data?.binCode ?? '',
            binId: data?.binId ?? null,
            snBinId: resolvedBatchId,
            quantity: resolvedQty,
          },
        ]
      : [];

    const newLine = {
      ...defaultDocumentLine,
      id: uuidv4(),
      itemCode: data.itemCode,
      itemName: data.item?.itemName || null,
      qty: resolvedQty,
      whsCode: data.whsCode || config.defaultWarehouse || null,
      quantityUnitId: data.item?.quantityUnit?.id,
      quantityUnitCode: data.item?.quantityUnit?.code,
      quantityUnitName: data.item?.quantityUnit?.name,
      baseId: selectedProduction?.id,
      baseLine: null,
      baseDocnum: selectedProduction?.workingNumber,
      baseDocType: null,
      docLineBatchSn: nextBatch,
      docLineBin: nextBin,
    };
    setDocLines((prev) => [...prev, newLine]);
    setItemFilter('');
  };

  useEffect(() => {
    if (selectedItem) {
      addItem(selectedItem);
      setSelectedItem(null);
      setItemFilter('');
    }
  }, [selectedItem]);

  const searchItem = () => {
    const val = itemFilter.trim();
    if (!val) return;

    if (!selectedProduction?.id) {
      addNotification({
        content: 'Először válassz egy gyártási utasítást.',
        type: NotificationType.WARNING,
      });
      return;
    }

    showLoader();

    const searchRequest = isIncoming
      ? productionService.checkItemForProdReturn({ query: val, fullSearch, productionId: selectedProduction?.id })
      : itemService.findItemFromStock({ query: val, fullSearch });

    searchRequest
      .then((data) => {
        if (data.length > 1) {
          setItems(data);
          return;
        }

        if (!data?.[0]?.itemCode) {
          addNotification({
            content: `Nincs találat a megadott cikkre. (${val})`,
            type: NotificationType.WARNING,
          });

          setTimeout(() => {
            setItemFilter('');
            if (itemInputRef.current) itemInputRef.current.focus();
          }, 500);

          return;
        }

        if (data?.length === 1 && data?.[0]?.itemCode) {
          setSelectedItem(data[0]);
        }
      })
      .finally(hideLoader);
  };

  const handleRemoveLine = (id) => {
    setDocLines((prev) => prev.filter((l) => l.id !== id));
  };

  // validate and submit
  const handleRegister = () => {
    const errors = [];
    const resolvedTargetWhsCode = String(targetWhsCode || '').trim();
    const targetWhs = warehouseData.find((warehouse) => warehouse.code === resolvedTargetWhsCode);
    const isTargetBinManaged = Boolean(targetWhs?.binManaged);
    const targetBin = (targetWhs?.bin ?? []).find((bin) => bin.binId === targetBinId) ?? null;

    if (!selectedProduction) errors.push('Nincs kiválasztott gyártási utasítás.');
    if (docLines.length === 0) errors.push('Nincs hozzáadott cikk.');
    if (docLines.some((l) => Number(l.qty) <= 0)) errors.push('Minden mennyiségnek 0-nál nagyobbnak kell lennie.');
    if (isIncoming && !resolvedTargetWhsCode) errors.push('Nincs megadott fogadó raktár!');
    if (isIncoming && isTargetBinManaged && !targetBin?.binId) errors.push('Nincs megadott fogadó tárhely!');

    docLines.forEach((line) => {
      const lineQty = Number(line.qty) || 0;

      if (Array.isArray(line.docLineBatchSn) && line.docLineBatchSn.length > 0) {
        const invalidBatch = line.docLineBatchSn.some(
          (batch) => !batch?.batchSn || String(batch.batchSn).trim() === ''
        );
        if (invalidBatch) {
          errors.push(`A(z) ${line.itemCode} cikknél hiányzik a sarzs.`);
        }

        const batchQty = line.docLineBatchSn.reduce((sum, batch) => sum + (Number(batch?.quantity) || 0), 0);
        if (batchQty !== lineQty) {
          errors.push(
            `A(z) ${line.itemCode} cikknél a sarzs mennyiség (${batchQty}) nem egyezik a sor mennyiségével (${lineQty}).`
          );
        }
      }

      if (Array.isArray(line.docLineBin) && line.docLineBin.length > 0) {
        const invalidBin = line.docLineBin.some((bin) => !bin?.binCode || String(bin.binCode).trim() === '');
        if (invalidBin) {
          errors.push(`A(z) ${line.itemCode} cikknél hiányzik a tárhely.`);
        }

        const binQty = line.docLineBin.reduce((sum, bin) => sum + (Number(bin?.quantity) || 0), 0);
        if (binQty !== lineQty) {
          errors.push(
            `A(z) ${line.itemCode} cikknél a tárhely mennyiség (${binQty}) nem egyezik a sor mennyiségével (${lineQty}).`
          );
        }
      }
    });

    if (errors.length) {
      addNotification({
        content: errors.join(' /n'),
        type: NotificationType.ERROR,
      });
      return;
    }

    const doc = {
      ...cleanValues(defaultValues),
      docTypeId: isIncoming ? DocumentType.ReturnFromProduction : DocumentType.IssueForProduction,
      ...(isIncoming ? { whsCode: resolvedTargetWhsCode } : {}),
      docLine: docLines.map((l) =>
        cleanValues(
          isIncoming
            ? {
                ...l,
                whsCode: resolvedTargetWhsCode,
                toBinId: isTargetBinManaged ? targetBin?.binId ?? null : null,
                docLineBin:
                  isTargetBinManaged && targetBin
                    ? l.docLineBatchSn?.length
                      ? l.docLineBatchSn.map((batch) => ({
                          id: uuidv4(),
                          binCode: targetBin.binCode ?? '',
                          binId: targetBin.binId ?? null,
                          snBinId: batch.id ?? null,
                          quantity: batch.quantity ?? 0,
                        }))
                      : [
                          {
                            id: uuidv4(),
                            binCode: targetBin.binCode ?? '',
                            binId: targetBin.binId ?? null,
                            snBinId: null,
                            quantity: l.qty ?? 0,
                          },
                        ]
                    : [],
              }
            : l
        )
      ),
      createdBy: user.userId,
    };

    const postRequest = isIncoming
      ? productionService.createProductionReturn(doc, selectedProduction.id)
      : productionService.createProductionIssue(doc, selectedProduction.id);

    postRequest.then(() => {
      addNotification({
        content: isIncoming
          ? 'A gyártás visszavét sikeresen rögzítve lett.'
          : 'A gyártás kiadás sikeresen rögzítve lett.',
        type: NotificationType.SUCCESS,
      });
      setSelectedProduction(null);
      setDocLines([]);
      setSearchQuery('');
      setOpenSearch(true);
    });
  };

  const isValid =
    !!selectedProduction &&
    docLines.length > 0 &&
    docLines.every((l) => Number(l.qty) > 0) &&
    (!isIncoming ||
      (() => {
        const resolvedTargetWhsCode = String(targetWhsCode || '').trim();
        if (!resolvedTargetWhsCode) return false;

        const whs = warehouseData.find((warehouse) => warehouse.code === resolvedTargetWhsCode);
        const isBinManaged = Boolean(whs?.binManaged);
        if (!isBinManaged) return true;

        return Boolean((whs?.bin ?? []).some((bin) => bin.binId === targetBinId));
      })());

  return (
    <PhoneBase name="ProductionMaterial" sx={{ p: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        {isIncoming ? 'Visszavét gyártásról' : 'Kiadás gyártásra'}
      </Typography>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          mb: 1.5,
        }}
      >
        <Box
          onClick={() => setIssueDirection(ProductionIssueDirection.OUTGOING)}
          sx={{
            px: 2,
            py: 0.75,
            cursor: 'pointer',
            bgcolor: !isIncoming ? 'action.selected' : 'transparent',
          }}
        >
          Kiadás
        </Box>
        <Box
          onClick={() => setIssueDirection(ProductionIssueDirection.INCOMING)}
          sx={{
            px: 2,
            py: 0.75,
            cursor: 'pointer',
            borderLeft: '1px solid',
            borderColor: 'divider',
            bgcolor: isIncoming ? 'action.selected' : 'transparent',
          }}
        >
          Visszavét
        </Box>
      </Box>
      <Box>
        <Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 80,
              mt: 1.5,
              p: 1.5,
              bgcolor: 'grey.100',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h7" component="h4">
                Gyártási utasítás
              </Typography>
              <IconButton size="small" onClick={handleOpenSearch} aria-label="Keresés">
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>

            {selectedProduction && (
              <Box
                key={selectedProduction.id}
                sx={{
                  px: 1.5,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: 'white',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {selectedProduction.workingNumber} #{selectedProduction.id}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Késztermék:
                  </Box>{' '}
                  {selectedProduction.articleNumber} - {selectedProduction.articleName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Gyártandó mennyiség:
                  </Box>{' '}
                  {selectedProduction.productionQuantity} {selectedProduction.quantityUnit?.code}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 80,
            mt: 1.5,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="h7" component="h4">
            Cikkek
          </Typography>

          {/* item filter input */}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, bgcolor: 'white' }}>
              <InputUI
                autoFocus
                fullWidth
                size="small"
                placeholder={fullSearch ? 'Cikknév/cikkszám' : 'Cikkszám/vonalkód'}
                value={itemFilter}
                onChange={(e) => setItemFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    searchItem();
                  }
                }}
                inputRef={itemInputRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" edge="end" aria-label="Keresés" onClick={searchItem}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <SwitchUI checked={fullSearch} onChange={(e) => setFullSearch(e.target.checked)} />
          </Box>

          {/* rögzített cikkek */}
          <Stack spacing={0.25} sx={{ mt: 1.5 }}>
            {docLines.map((item) => {
              const key = item.id;
              const batchText = (item.docLineBatchSn ?? [])
                .map((batch) => batch?.batchSn)
                .filter((batchSn) => Boolean(batchSn && String(batchSn).trim() !== ''))
                .join(', ');
              const binText = (item.docLineBin ?? [])
                .map((bin) => bin?.binCode)
                .filter((binCode) => Boolean(binCode && String(binCode).trim() !== ''))
                .join(', ');

              return (
                <Box
                  key={key}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'white',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {item.itemCode} - {item.itemName}
                      </Typography>
                      {batchText ? (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            Sarzs:{' '}
                          </Box>
                          {batchText}
                        </Typography>
                      ) : null}
                      {binText ? (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            Tárhely:{' '}
                          </Box>
                          {binText}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InputUI
                        name="qty"
                        type="number"
                        size="small"
                        sx={{ width: 110 }}
                        inputProps={{ min: 0, step: 1 }}
                        value={item.qty ?? 0}
                        onChange={handleQtyChange(key)}
                      />
                      <IconButton color="error" size="small" aria-label="Törlés" onClick={() => handleRemoveLine(key)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>

        {isIncoming ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 80,
              mt: 1.5,
              p: 1.5,
              bgcolor: 'grey.100',
            }}
          >
            <Typography variant="h7" component="h4">
              Cél raktár
            </Typography>

            <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Fogadó raktár" />}
                InputLabelProps={{ shrink: Boolean(targetWhsCode) }}
                selectedValue={targetWhsCode}
                selectedLabelValue={warehouseOptions.find((w) => w.value === targetWhsCode)?.label ?? ''}
                onChange={(_e, newVal) => {
                  const nextWhsCode = newVal?.value ?? '';
                  setTargetWhsCode(nextWhsCode);

                  const whs = warehouseData.find((warehouse) => warehouse.code === nextWhsCode);
                  if (whs?.binManaged) {
                    setTargetBinId(whs.dispatchBin ?? null);
                  } else {
                    setTargetBinId(null);
                  }
                }}
                dataset={warehouseOptions}
              />
              {(() => {
                const whs = warehouseData.find((warehouse) => warehouse.code === targetWhsCode);
                const isBinManaged = Boolean(whs?.binManaged);
                if (!isBinManaged) return null;

                const binOptions = generateAutocompleteItems(whs?.bin ?? [], 'binCode', 'binId');
                const selectedBin = (whs?.bin ?? []).find((bin) => bin.binId === targetBinId);

                return (
                  <AutoCompleteSelectUI
                    label={<FormLabelUI text="Fogadó tárhely" />}
                    InputLabelProps={{ shrink: Boolean(targetBinId) }}
                    selectedValue={targetBinId}
                    selectedLabelValue={selectedBin?.binCode ?? ''}
                    onChange={(_e, newVal) => {
                      setTargetBinId(newVal?.value ?? null);
                    }}
                    dataset={binOptions}
                  />
                );
              })()}
            </Box>
          </Box>
        ) : null}

        <Box textAlign="center" mt="2rem">
          <ButtonUI
            text={isIncoming ? 'Visszavét rögzítése' : 'Kiadás rögzítése'}
            className="bg-success"
            onClick={handleRegister}
            disabled={!isValid}
          />
        </Box>
      </Box>

      <SearchDialog
        open={openSearch}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        handleClose={handleCloseSearch}
        setSelectedProduction={setSelectedProduction}
      />

      <ItemsDialog
        open={items.length > 0}
        handleClose={() => {
          setItems([]);
          setItemFilter('');
          if (itemInputRef.current) itemInputRef.current.focus();
        }}
        items={items}
        addItem={addItem}
      />
    </PhoneBase>
  );
};

export default ProductionMaterial;
